import {
  Box,
  type CheckboxProps,
  chakra,
  Icon,
  type IconProps,
  type LayoutProps,
  type useCheckbox,
} from '@chakra-ui/react';
import type { SVGProps } from 'react';

import styles from './circle-checkbox.module.css';

const D_EMPTY =
  'M19.5 10C19.5 15.2467 15.2467 19.5 10 19.5C4.7533 19.5 0.5 15.2467 0.5 10C0.5 4.7533 4.7533 0.5 10 0.5C15.2467 0.5 19.5 4.7533 19.5 10Z';

const D_CHECKED_BG =
  'M20 10C20 15.5228 15.5228 20 10 20C4.47715 20 0 15.5228 0 10C0 4.47715 4.47715 0 10 0C15.5228 0 20 4.47715 20 10Z';

const D_CHECKED_MARK =
  'M16.4705 6.33976L8.30328 16.4706L3.5293 11.8678L5.09021 10.0098L8.094 12.9059L14.7046 4.70593L16.4705 6.33976Z';

/* ── Plain SVG icons (no Chakra) ── */

interface CircleCheckIconProps extends SVGProps<SVGSVGElement> {
  size?: number | string;
}

export function CircleCheckEmptyIcon({
  size = 24,
  ...props
}: CircleCheckIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path d={D_EMPTY} stroke="#2D426A" strokeOpacity="0.2" />
    </svg>
  );
}

export function CircleCheckedIcon({
  size = 24,
  ...props
}: CircleCheckIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path fill="#3BCE7B" d={D_CHECKED_BG} />
      <path fill="white" d={D_CHECKED_MARK} />
    </svg>
  );
}

/* ── Chakra-based components (existing API) ── */

interface CircleCheckboxProps extends CheckboxProps {
  checkBoxApi: ReturnType<typeof useCheckbox>;
  /* Opt-in theming of the checked fill (#3BCE7B → Figma green under
     body[data-theme]). Currently only set by the report-builder card variant;
     unset elsewhere keeps every existing usage byte-identical. */
  themed?: boolean;
}

export function CircleCheckbox({
  checkBoxApi,
  themed,
  ...props
}: CircleCheckboxProps) {
  const { state } = checkBoxApi;

  const { height = '24px', width = '24px', opacity } = props;

  return (
    <chakra.label
      display="contents"
      border="1px solid"
      borderRadius="50%"
      borderColor="gray.300"
      cursor="pointer"
    >
      <Box>
        {state.isChecked ? (
          <CircleChecked
            width={width}
            height={height}
            themed={themed}
            iconProps={{
              opacity,
              ...(themed ? { className: styles['checked'] } : {}),
            }}
          />
        ) : (
          <Icon
            w={width}
            h={height}
            viewBox="0 0 20 20"
            {...(themed ? { className: styles['empty'] } : {})}
          >
            <path
              fill="none"
              d={D_EMPTY}
              stroke="#2D426A"
              strokeOpacity="0.2"
            />
          </Icon>
        )}
      </Box>
    </chakra.label>
  );
}

type CircleCheckedProps = {
  height?: LayoutProps['h'];
  width?: LayoutProps['w'];
  iconProps?: IconProps;
  /* When set (report-builder variant), render BOTH the legacy checkmark and the
     Figma centre dot; the co-located CSS shows the checkmark in legacy and swaps
     to the dot under body[data-theme] (138:17151 dark / 138:18460 light), so the
     un-themed appearance stays byte-identical. */
  themed?: boolean;
};
export function CircleChecked({
  width,
  height,
  iconProps,
  themed,
}: CircleCheckedProps) {
  return (
    <Icon w={width} h={height} {...iconProps} viewBox="0 0 20 20">
      <path fill="#3BCE7B" d={D_CHECKED_BG} />
      {themed ? (
        <>
          <path
            className={styles['checkMark']}
            fill="white"
            d={D_CHECKED_MARK}
          />
          <circle
            className={styles['checkDot']}
            cx="10"
            cy="10"
            r="5"
            fill="white"
          />
        </>
      ) : (
        <path fill="white" d={D_CHECKED_MARK} />
      )}
    </Icon>
  );
}
